var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { AwsUi } from '../../../../core/runtime/src';
import LegacyClassBuilder from '../../../../core/utils/src/class-builders/legacy';
import { Icon } from '../../../icon/src';
import { ComponentFeature } from '../internal';
import { getCollectionComponent, renderSafely, ChangeHandler } from '../utility';
// TODO [mjawors] _.range
function range(from, to) {
    var result = [];
    for (var i = from; i <= to; i++) {
        result.push(i);
    }
    return result;
}
var __updateController = function (oldValue, newValue) {
    if (oldValue !== newValue && this.__controller) {
        this.__controller.processComponentChange(ComponentFeature.PAGINATION, this.__getData(), false);
    }
};
var __handleChange = function () {
    var args = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
    }
    this.__changeHandler.apply(this, args);
};
function getPaginationState(currentPageIndex, totalPagesCount, isOpenEnd) {
    // Total number of elements handled here
    var numberOfControls = 7;
    // Max number of controls that can be displayed on the left and right hand side of the current page.
    // Works only for odd numbers
    var leftDelta = Math.floor(numberOfControls / 2);
    var rightDelta = leftDelta;
    // upper and lower limits for pages range
    var lowerLimit = 2;
    var upperLimit = totalPagesCount - 1;
    if (isOpenEnd) {
        rightDelta++;
        upperLimit = totalPagesCount + 1;
    }
    // Left and right indices based on delta calculation
    var leftIndex = currentPageIndex - leftDelta;
    var rightIndex = currentPageIndex + rightDelta;
    // adjust page indexes if page index is too small
    if (leftIndex < lowerLimit) {
        rightIndex += lowerLimit - leftIndex;
        leftIndex = lowerLimit;
    }
    // adjust page indexes if page index is to big
    if (rightIndex > upperLimit) {
        leftIndex -= rightIndex - upperLimit;
        rightIndex = upperLimit;
    }
    // adjust indexes one more time to avoid out of range errors
    leftIndex = Math.max(leftIndex, 2);
    rightIndex = Math.min(rightIndex, upperLimit);
    // consider adding dots
    var leftDots = leftIndex > 2;
    var rightDots = isOpenEnd || rightIndex < upperLimit;
    if (leftDots) {
        leftIndex++;
    }
    if (rightDots) {
        rightIndex--;
    }
    return { leftDots: leftDots, rightDots: rightDots, leftIndex: leftIndex, rightIndex: rightIndex };
}
var _a = AwsUi.__addComponents(['awsui-table-pagination', 'awsui-cards-pagination'], {
    releaseStatus: 'stable',
    events: {
        // Fired when a user interaction triggers a pagination change.
        // The event `detail` contains the `currentPageIndex`, the `pagesCount`
        // and the `pageSize`.
        //
        // This event is also fired when the user selects a different page size in the preferences modal.
        //
        // In compliance with our general strategy, the event is not fired when properties are modified
        // through the component API.
        // However, when setting a value on the page-size-selector component causes a change in the `currentPageIndex`
        // (because that page is no longer available) then the event is fired.
        paginationChange: {
            bubbles: true,
            cancelable: false,
            detailType: 'TablePagination.PaginationChangeDetail'
        },
        // Fired when the previous page arrow is clicked. The event `detail` contains
        // * `requestedPageAvailable`: always `true`.
        // * `requestedPageIndex`: the index of the requested page.
        previousPageClick: {
            bubbles: true,
            cancelable: false,
            detailType: 'TablePagination.PageClickDetail'
        },
        // Fired when the next page arrow is clicked. The event `detail` contains
        // * `requestedPageAvailable`: a boolean indicating whether the requested page is available for display.
        //   The value can be false when the `openEnd` property is set to `true`.
        // * `requestedPageIndex`: the index of the requested page.
        nextPageClick: {
            bubbles: true,
            cancelable: false,
            detailType: 'TablePagination.PageClickDetail'
        }
    },
    properties: {
        // Sets the pagination variant. It can be either default (when setting it to
        // `false`) or open ended (when setting it to `true`).
        // Default pagination will navigate you through the items list, while the open end
        // variant will allow you to lazy-load your items because it always displays three dots before
        // the next page icon. The next page button will never be disabled. When the user clicks on it
        // but there are no more items to show, the `awsui:nextPageClick` event will be fired with
        // `requestedPageAvailable=false`.
        openEnd: {
            type: 'boolean'
        },
        // Index of the current page. The first page has index 1.
        //
        // If the specified `currentPageIndex` is out of bounds (lower than 1 or greater than the
        // total pages count) it will automatically be set to `1`.
        currentPageIndex: {
            type: 'integer',
            defaultValue: 1,
            onChange: __handleChange
        },
        // Sets the number of pages externally (example: server-side pagination). When this property is defined,
        // the component will not automatically paginate items. All items will be displayed, so the `items` property should contain
        // only the content of the current page. Use `awsui:paginationChange` event to load different pages on users' demand.
        //
        // Only positive integer numbers are allowed. Zero will be ignored.
        pagesCount: {
            type: 'integer',
            onChange: __handleChange
        },
        // If set to `true`, the pagination links will be disabled.
        //
        // Use it, for example, if you want to prevent the user from changing page before items are loaded.
        disabled: {
            type: 'boolean'
        },
        // Adds aria-labels to the pagination buttons:
        // * previous button: `previousPageLabel` (string)
        // * page button: `pageLabel` (number => string) we call this function with every page number to be rendered
        // * next button: `nextPageLabel` (string)
        labels: {
            type: 'object',
            objectType: 'TablePagination.PaginationLabels'
        },
        __totalPagesCount: {
            type: 'integer',
            optional: true
        },
        // Value of the desired page size.
        pageSize: {
            type: 'integer',
            // the DEFAULT_PAGE_SIZE constant can't be used here since our component definition doesn't detect any variable.
            defaultValue: 10,
            onChange: function (oldValue, newValue) {
                if (this.__controller) {
                    this.__controller.setPageSize(newValue, false);
                }
            }
        }
    },
    callbacks: {
        initialized: function () {
            this.__changeHandler = new ChangeHandler(__updateController);
        },
        attached: function () {
            this.__parentComponent = getCollectionComponent(this.node);
            this.__parentComponent.__pagination = this;
            this.__controller = this.__parentComponent.__controller;
            this.__controller.setPageSize(this.pageSize);
        },
        detached: function () {
            if (this.__parentComponent) {
                this.__parentComponent.__pagination = null;
                this.__controller = null;
            }
        }
    },
    functions: {
        __getData: function () {
            return {
                currentPageIndex: this.currentPageIndex,
                pageSize: this.pageSize,
                pagesCount: this.pagesCount
            };
        },
        __getEventData: function () {
            return {
                pagesCount: this.pagesCount || this.__totalPagesCount,
                currentPageIndex: this.currentPageIndex,
                pageSize: this.pageSize
            };
        },
        __updateData: function (paginationData, fireEvent) {
            var _this = this;
            this.__changeHandler.prevent(function () {
                _this.currentPageIndex = paginationData.currentPageIndex;
                _this.__totalPagesCount = paginationData.totalPagesCount;
                _this.pageSize = paginationData.pageSize;
            });
            if (fireEvent) {
                this.__fireEvent('paginationChange', this.__getEventData());
            }
        }
    },
    render: renderSafely(function (m, h) {
        var _this = this;
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        var component = this;
        var labels = component.labels || {
            nextPageLabel: '',
            previousPageLabel: '',
            pageLabel: function (pageNumber) { return "" + pageNumber; }
        };
        var currentPageIndex = component.currentPageIndex;
        var pagesCount = this.pagesCount || component.__totalPagesCount || 1;
        var handleClick = function (event, pageIndex) {
            event.preventDefault();
            var currentData = _this.__getData();
            currentData.currentPageIndex = pageIndex;
            _this.__controller.processComponentChange(ComponentFeature.PAGINATION, currentData, true);
        };
        function onClick(pageIndex) {
            return function (event) { return handleClick(event, pageIndex); };
        }
        function onIconClick(pageIndex, direction, requestedPageAvailable) {
            var events = {
                prev: 'previousPageClick',
                next: 'nextPageClick'
            };
            return function (event) {
                if (requestedPageAvailable) {
                    handleClick(event, pageIndex);
                }
                component.__fireEvent(events[direction], {
                    requestedPageAvailable: requestedPageAvailable,
                    requestedPageIndex: pageIndex
                });
            };
        }
        function prepareButtonAttributes(onClick, disabled, label, current) {
            if (current === void 0) { current = false; }
            var attributes = {
                type: 'button',
                'aria-label': label
            };
            if (disabled) {
                attributes.tabindex = '-1';
                attributes['aria-disabled'] = 'true';
            }
            else {
                attributes.onclick = onClick;
                attributes['override-focus'] = 'true';
            }
            if (current) {
                attributes['aria-current'] = 'true';
            }
            return attributes;
        }
        function pageIconButton(pageIndex, direction) {
            var icons = {
                prev: 'angle-left',
                next: 'angle-right'
            };
            var isOutOfRange = pageIndex < 1 || pageIndex > pagesCount;
            var disableIconButton = component.disabled || (isOutOfRange && (direction === 'prev' || !component.openEnd));
            var cb = new LegacyClassBuilder(component)
                .addComponentClass(direction + "-page")
                .addComponentClass("page-control-disabled", disableIconButton);
            var attributes = prepareButtonAttributes(onIconClick(pageIndex, direction, !isOutOfRange), disableIconButton, direction === 'prev' ? labels.previousPageLabel : labels.nextPageLabel);
            return (AwsUi.__createElement("li", { className: cb.toClassName() },
                AwsUi.__createElement("button", __assign({}, attributes),
                    AwsUi.__createElement(Icon, { name: icons[direction], variant: disableIconButton ? 'disabled' : 'normal' }))));
        }
        function pageNumberButton(pageIndex) {
            var isCurrentPage = pageIndex === currentPageIndex;
            var listItemCb = new LegacyClassBuilder(component).addComponentClass('page-number');
            var buttonCb = new LegacyClassBuilder(component).addComponentClass('current-page', isCurrentPage);
            var attributes = prepareButtonAttributes(onClick(pageIndex), component.disabled, labels.pageLabel(pageIndex), isCurrentPage);
            return (AwsUi.__createElement("li", { className: listItemCb.toClassName() },
                AwsUi.__createElement("button", __assign({}, attributes, { className: buttonCb.toClassName() }), pageIndex)));
        }
        var cbDots = new LegacyClassBuilder(component).addComponentClass('dots');
        var getDots = function () { return AwsUi.__createElement("li", { className: cbDots.toClassName() }, "..."); };
        var _a = getPaginationState(currentPageIndex, pagesCount, this.openEnd), leftDots = _a.leftDots, leftIndex = _a.leftIndex, rightIndex = _a.rightIndex, rightDots = _a.rightDots;
        var cbContent = new LegacyClassBuilder(component)
            .addComponentClass('content')
            .addComponentClass('disabled', this.disabled);
        return (AwsUi.__createElement("ul", { className: cbContent.toClassName() },
            pageIconButton(currentPageIndex - 1, 'prev'),
            pageNumberButton(1),
            leftDots ? getDots() : '',
            range(leftIndex, rightIndex).map(function (pageNumber) { return pageNumberButton(pageNumber); }),
            rightDots ? getDots() : '',
            !this.openEnd && pagesCount > 1 ? pageNumberButton(pagesCount) : '',
            pageIconButton(currentPageIndex + 1, 'next')));
    })
}), TablePagination = _a[0], CardsPagination = _a[1];
export { TablePagination, CardsPagination };
//# sourceMappingURL=index.js.map